import React, { useState, useEffect, useRef } from 'react'
import * as api from '../../lib/api'
import './PublicVacancyPage.css'

function CustomSelect({ value, onChange, options, required, placeholder = 'Выберите...' }) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [])

  return (
    <div className="custom-select-container" ref={containerRef}>
      <div 
        className={`custom-select-trigger ${isOpen ? 'open' : ''} ${required && !value ? 'invalid' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? 'custom-select-value' : 'custom-select-placeholder'}>
          {value || placeholder}
        </span>
        <span className="custom-select-arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </div>
      
      {/* Hidden native select for validation */}
      <select 
        value={value || ''} 
        readOnly
        required={required} 
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: '100%', height: '100%', left: 0, top: 0 }}
      >
        <option value="">{placeholder}</option>
        {(options || []).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>

      {isOpen && (
        <div className="custom-select-dropdown">
          <div 
            className={`custom-select-option placeholder-option ${!value ? 'selected' : ''}`}
            onClick={() => {
              onChange('')
              setIsOpen(false)
            }}
          >
            {placeholder}
          </div>
          {(options || []).map((opt) => (
            <div 
              key={opt}
              className={`custom-select-option ${value === opt ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt)
                setIsOpen(false)
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


export default function PublicVacancyPage({ slug }) {
  const [vacancy, setVacancy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const [formData, setFormData] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [createdResponseId, setCreatedResponseId] = useState(null)
  const [copiedLink, setCopiedLink] = useState(false)
  useEffect(() => {
    document.body.classList.add('public-page')
    document.documentElement.classList.add('public-page')
    return () => {
      document.body.classList.remove('public-page')
      document.documentElement.classList.remove('public-page')
    }
  }, [])

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getPublicVacancy(slug)
        setVacancy(data)
      } catch (err) {
        setError(err.message || 'Вакансия не найдена или закрыта')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)

    // Извлекаем обязательные системные поля, остальные кидаем в answers
    const payload = {
      full_name: formData.full_name || '',
      phone: formData.phone || '',
      source: 'website',
      answers: { ...formData }
    }

    try {
      const response = await api.submitVacancyResponse(slug, payload)
      if (response && response.id) {
        setCreatedResponseId(response.id)
      }
      setSuccess(true)
    } catch (err) {
      alert('Ошибка при отправке: ' + err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }))
  }

  if (loading) {
    return (
      <div className="public-vacancy-page">
        <div className="public-vacancy-loading">Загрузка вакансии...</div>
      </div>
    )
  }

  if (error || !vacancy) {
    return (
      <div className="public-vacancy-page">
        <div className="public-vacancy-error card">
          <h2>Упс!</h2>
          <p>{error || 'Вакансия не найдена'}</p>
        </div>
      </div>
    )
  }

  const handleCopyLink = () => {
    const trackingUrl = `${window.location.origin}/vacancy-response/${createdResponseId}`
    navigator.clipboard.writeText(trackingUrl)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  if (success) {
    return (
      <div className="public-vacancy-page">
        <div className="public-vacancy-success card animate-scale-in">
          <div className="success-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="20 6 9 17 4 12"/></svg></div>
          <h2>Анкета отправлена!</h2>
          <p>Спасибо за отклик на вакансию <b>{vacancy.title}</b>.</p>
          <p>Мы внимательно изучим вашу анкету и скоро свяжемся с вами по номеру {formData.phone}.</p>
          
          {createdResponseId && (
            <div className="tracking-link-card">
              <p>Сохраните ссылку для отслеживания статуса отклика:</p>
              <div className="tracking-link-wrapper">
                <a href={`/vacancy-response/${createdResponseId}`} target="_blank" rel="noopener noreferrer" className="tracking-link-url">
                  {window.location.origin}/vacancy-response/{createdResponseId.slice(0, 15)}...
                </a>
                <button type="button" className="btn-copy-link" onClick={handleCopyLink} title="Копировать ссылку">
                  {copiedLink ? (
                    <span style={{ fontSize: '11px', color: 'var(--success)', fontWeight: 600 }}>Скопировано!</span>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const fields = vacancy.form_fields || [
    { id: 'full_name', label: 'Имя', type: 'text', required: true },
    { id: 'phone', label: 'Телефон', type: 'phone', required: true },
    { id: 'experience', label: 'Опыт работы', type: 'textarea', required: false }
  ]

  return (
    <div className="public-vacancy-page">
      <div className="public-vacancy-container">
        <div className="public-vacancy-header card">
          <h1>{vacancy.title}</h1>
          <div className="vacancy-tags">
            {(vacancy.salary_from || vacancy.salary_to) && (
              <span className="vacancy-tag highlight">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'4px'}}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>{vacancy.salary_from ? `от ${vacancy.salary_from.toLocaleString('ru')} ` : ''}
                {vacancy.salary_to ? `до ${vacancy.salary_to.toLocaleString('ru')} ` : ''} ₽
              </span>
            )}
            {vacancy.schedule && <span className="vacancy-tag"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'4px'}}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>{vacancy.schedule}</span>}
            {vacancy.address && <span className="vacancy-tag"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'4px'}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{vacancy.address}</span>}
          </div>
          
          {vacancy.description && (
            <div className="vacancy-block">
              <h3>О работе</h3>
              <p>{vacancy.description}</p>
            </div>
          )}
          
          {vacancy.requirements && (
            <div className="vacancy-block">
              <h3>Требования</h3>
              <ul className="vacancy-list">
                {vacancy.requirements.split('\n').filter(r => r.trim()).map((r, i) => (
                  <li key={i}>{r.replace(/^[-•]\s*/, '')}</li>
                ))}
              </ul>
            </div>
          )}
          
          {vacancy.benefits && (
            <div className="vacancy-block">
              <h3>Мы предлагаем</h3>
              <ul className="vacancy-list">
                {vacancy.benefits.split('\n').filter(r => r.trim()).map((r, i) => (
                  <li key={i}>{r.replace(/^[-•]\s*/, '')}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <form className="public-vacancy-form card" onSubmit={handleSubmit}>
          <h2>Откликнуться на вакансию</h2>
          <p className="form-subtitle">Оставьте свои данные, и мы с вами свяжемся.</p>

          <div className="form-fields">
            {fields.filter(f => f.enabled !== false).map(field => (
              <div key={field.id} className="input-group">
                <label>
                  {field.label} {field.required && <span className="req">*</span>}
                </label>
                
                {field.type === 'textarea' ? (
                  <textarea
                    className="input"
                    required={field.required}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    rows={3}
                  />
                ) : field.type === 'select' ? (
                  <CustomSelect
                    value={formData[field.id] || ''}
                    onChange={(val) => handleChange(field.id, val)}
                    options={field.options || []}
                    required={field.required}
                  />
                ) : field.type === 'phone' ? (
                  <input
                    className="input"
                    type="tel"
                    required={field.required}
                    placeholder="+7 (999) 000-00-00"
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                  />
                ) : (
                  <input
                    className="input"
                    type={field.type || 'text'}
                    required={field.required}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>

          <button 
            type="submit" 
            className="btn btn-primary submit-btn" 
            disabled={submitting}
          >
            {submitting ? 'Отправка...' : 'Отправить анкету'}
          </button>
        </form>
      </div>
    </div>
  )
}
